import { useQuery } from "@tanstack/react-query";
import api, { num } from "@/lib/api";
import { PageHeader, Panel, Loading, StatCard, CHART_COLORS } from "@/components/kit";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
  PieChart, Pie, Legend,
} from "recharts";
import { CheckCircle2, Copy, XCircle, Unlink } from "lucide-react";

export default function DataQuality() {
  const { data, isLoading } = useQuery({
    queryKey: ["dash-dq"],
    queryFn: async () => (await api.get("/dashboard/data-quality")).data,
  });
  if (isLoading || !data) return <Loading label="Loading data quality dashboard…" />;
  const q = Object.fromEntries(data.invoice_quality.map((x) => [x.status, x.count]));

  return (
    <div>
      <PageHeader title="Data Quality" subtitle="Invoice validation, exception distribution and orphan records" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="dq-valid" label="Valid Records" value={num(q.valid || 0)} icon={CheckCircle2} tone="emerald" />
        <StatCard testid="dq-duplicate" label="Duplicates" value={num(q.duplicate || 0)} icon={Copy} tone="gold" />
        <StatCard testid="dq-invalid" label="Invalid Records" value={num(q.invalid || 0)} icon={XCircle} tone="crimson" />
        <StatCard testid="dq-orphan" label="Orphan / Unmapped" value={num(data.orphan_records)} icon={Unlink} tone="navy" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel testid="panel-ex-type" title="Exceptions by Type">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={data.exceptions_by_type} layout="vertical" margin={{ left: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
              <YAxis type="category" dataKey="type" tick={{ fontSize: 11, fill: "#334155" }} width={120} />
              <Tooltip />
              <Bar dataKey="count" radius={[0, 8, 8, 0]} barSize={20}>
                {data.exceptions_by_type.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel testid="panel-ex-severity" title="Exceptions by Severity">
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie data={data.exceptions_by_severity} dataKey="count" nameKey="severity" innerRadius={55} outerRadius={95} paddingAngle={3}>
                {data.exceptions_by_severity.map((e, i) => {
                  const c = { high: "#DC2626", medium: "#D97706", low: "#64748b" }[e.severity] || CHART_COLORS[i];
                  return <Cell key={i} fill={c} />;
                })}
              </Pie>
              <Tooltip />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </Panel>
      </div>
    </div>
  );
}
